import { useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useWizardLocking } from './WizardContext';

// UseFormReturn<any> is intentional — UseFormReturn<AnalysisInput> is non-assignable to it
// due to RHF's contravariant generic positions; using any keeps step components simple.
// biome-ignore lint/suspicious/noExplicitAny: see comment above
export type StepForm = UseFormReturn<any>;

interface WizardFieldProps {
  form: StepForm;
  name: string;
  label: string;
  /** percent: stored as decimal (0.05), displayed as percentage (5%). Converts on change.
   *  currency: stored as number (10000), displayed with $ prefix and commas ($10,000). */
  format?: 'percent' | 'currency';
  type?: 'text' | 'number' | 'select';
  options?: readonly { value: string; label: string }[];
  placeholder?: string;
  hint?: string;
  step?: number;
  min?: number;
  max?: number;
  /**
   * Cosmetic provenance pill shown in the label row (Task 1). `default` means
   * the prefilled value matches the state seed; `custom` means the org moved
   * it. Omit to render no pill.
   */
  originChip?: { label: string; tone: 'default' | 'custom' } | null;
}

/**
 * Thin wrapper over react-hook-form register + Tailwind styling.
 * Renders a label, an input (text/number/select), and an inline error.
 *
 * format="percent": displays stored decimal × 100 with a % suffix. Divides
 *   back by 100 before storing so the submitted payload is always a decimal.
 *
 * format="currency": displays stored number with $ prefix and comma grouping.
 *   Shows raw number while focused for easy editing; re-formats on blur.
 *
 * If the field's name is in ADR-029 LOCKED_FIELDS AND the viewer is not an
 * owner/consultant, the field renders read-only with a "Locked" badge.
 */
export function WizardField({
  form,
  name,
  label,
  format,
  type = 'text',
  options,
  placeholder,
  hint,
  step: stepAttr,
  min,
  max,
  originChip,
}: WizardFieldProps) {
  const { register, formState, control } = form;
  const locking = useWizardLocking();
  const locked = locking.isLocked(name);

  const error = name
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc !== null && acc !== undefined && typeof acc === 'object'
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      formState.errors,
    );
  const errorMessage = (error as { message?: string } | undefined)?.message;

  const inputClass = `block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 ${
    errorMessage
      ? 'border-red-400 bg-red-50'
      : locked
        ? 'border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed'
        : 'border-slate-300 bg-white'
  }`;

  const citationLabel = locked ? buildCitationLabel(name, locking) : null;

  const labelRow = (
    <div className="flex items-center justify-between gap-2">
      <label htmlFor={name} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      {originChip && (
        <span
          title={
            originChip.tone === 'default'
              ? 'This value matches your state defaults.'
              : 'This value has been customized away from your state defaults.'
          }
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
            originChip.tone === 'default'
              ? 'bg-teal-50 text-teal-700'
              : 'bg-amber-50 text-amber-700'
          }`}
        >
          {originChip.label}
        </span>
      )}
      {locked && citationLabel && (
        <span
          title={citationLabel.tooltip}
          className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600"
        >
          <LockGlyph />
          {citationLabel.badge}
        </span>
      )}
    </div>
  );

  const hintRow = hint && !errorMessage && !locked && (
    <p className="mt-1 text-xs text-slate-400">{hint}</p>
  );
  const lockedHint = locked && citationLabel && !errorMessage && (
    <p className="mt-1 text-xs text-slate-400">{citationLabel.tooltip}</p>
  );
  const errorRow = errorMessage && <p className="mt-1 text-xs text-red-600">{errorMessage}</p>;

  // ── Percent format ───────────────────────────────────────────────────────
  if (format === 'percent') {
    const displayStep = (stepAttr ?? 0.01) * 100;
    const displayMin = min != null ? min * 100 : undefined;
    const displayMax = max != null ? max * 100 : undefined;

    const toDisplay = (v: unknown): string => {
      const n = typeof v === 'number' ? v : Number(v);
      if (!Number.isFinite(n)) return '';
      // Avoid floating-point drift: 0.02 * 100 = 2.0000000000000004
      const pct = Math.round(n * 100 * 1e8) / 1e8;
      return String(pct);
    };

    return (
      <div>
        {labelRow}
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <div className="relative mt-1">
              <input
                id={name}
                type="number"
                step={displayStep}
                min={displayMin}
                max={displayMax}
                value={toDisplay(field.value)}
                onChange={(e) => {
                  const raw = e.target.value === '' ? null : Number(e.target.value);
                  field.onChange(raw == null || Number.isNaN(raw) ? null : raw / 100);
                }}
                onBlur={field.onBlur}
                disabled={locked}
                readOnly={locked}
                className={`${inputClass} pr-8`}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-400">
                %
              </span>
            </div>
          )}
        />
        {hintRow}
        {lockedHint}
        {errorRow}
      </div>
    );
  }

  // ── Currency format ──────────────────────────────────────────────────────
  if (format === 'currency') {
    return (
      <div>
        {labelRow}
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <CurrencyInput field={field} name={name} inputClass={inputClass} locked={locked} />
          )}
        />
        {hintRow}
        {lockedHint}
        {errorRow}
      </div>
    );
  }

  // ── Default (text / number / select) ────────────────────────────────────
  const registration = register(name, type === 'number' ? { valueAsNumber: true } : {});

  return (
    <div>
      {labelRow}
      {type === 'select' && options ? (
        <select id={name} className={`mt-1 ${inputClass}`} disabled={locked} {...registration}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          step={stepAttr}
          min={min}
          max={max}
          placeholder={placeholder}
          className={`mt-1 ${inputClass}`}
          disabled={locked}
          readOnly={locked}
          {...registration}
        />
      )}
      {hintRow}
      {lockedHint}
      {errorRow}
    </div>
  );
}

// ── CurrencyInput sub-component ──────────────────────────────────────────────
// Extracted so it can call useState (hooks can't live in a render prop directly).

interface CurrencyInputProps {
  // biome-ignore lint/suspicious/noExplicitAny: field type from Controller render prop
  field: { value: any; onChange: (v: number) => void; onBlur: () => void };
  name: string;
  inputClass: string;
  locked: boolean;
}

function CurrencyInput({ field, name, inputClass, locked }: CurrencyInputProps) {
  const [focused, setFocused] = useState(false);
  const stored = field.value as number | undefined;
  const fmt = new Intl.NumberFormat('en-US');

  const displayValue = focused
    ? stored != null && stored !== 0 && !Number.isNaN(stored)
      ? String(stored)
      : ''
    : stored != null && !Number.isNaN(stored)
      ? fmt.format(stored)
      : '0';

  return (
    <div className="relative mt-1">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-400">
        $
      </span>
      <input
        id={name}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={(e) => {
          const stripped = e.target.value.replace(/[$,\s]/g, '');
          const n = stripped === '' ? 0 : Number(stripped);
          field.onChange(Number.isFinite(n) ? n : 0);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          field.onBlur();
        }}
        disabled={locked}
        readOnly={locked}
        className={`${inputClass} pl-7`}
      />
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function LockGlyph() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className="h-3 w-3"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path d="M3.5 5.5V4a2.5 2.5 0 0 1 5 0v1.5" />
      <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
    </svg>
  );
}

function buildCitationLabel(
  name: string,
  locking: ReturnType<typeof useWizardLocking>,
): { badge: string; tooltip: string } {
  const isJurisdiction = name.startsWith('step6.');
  const citation = isJurisdiction ? locking.jurisdictionCitation : locking.assumptionCitation;

  if (!citation) {
    return {
      badge: 'Locked',
      tooltip: 'Locked by admin. Ask an owner to change this value in Settings.',
    };
  }

  const parts: string[] = [`Locked · ${citation.name}`];
  if (citation.lastEditedBy || citation.lastEditedAt) {
    const who = citation.lastEditedBy ? `by ${citation.lastEditedBy}` : '';
    const when = citation.lastEditedAt
      ? `on ${new Date(citation.lastEditedAt).toLocaleDateString()}`
      : '';
    parts.push(`Set ${[who, when].filter(Boolean).join(' ')}`.trim());
  }
  return {
    badge: 'Locked',
    tooltip: parts.join(' · '),
  };
}
